// The hub: the hero (with the match being played now and what's up next),
// the event finder, featured events, open registrations, then four compact
// panels: the week's schedule, top teams, recent results and announcements.
import { Link } from 'react-router'
import { m } from 'framer-motion'
import { ArrowRight, CalendarClock, ClipboardList, Compass, Megaphone, Radio, RefreshCw, Star, Swords, Trophy, Users } from 'lucide-react'
import { FadedPhoto } from '../components/brand/FadedPhoto.jsx'
import { EventFinder } from '../components/hub/EventFinder.jsx'
import {
  AnnouncementsPanelList,
  HomeEventCard,
  HomeRegistrationCard,
  LiveMatch,
  Panel,
  ResultsPanelList,
  SchedulePanelList,
  TopTeamsPanelList,
  UpNextList,
} from '../components/hub/HomeCards.jsx'
import { Button } from '../components/ui/Button.jsx'
import { Card, Container, EmptyState, Skeleton } from '../components/ui/primitives.jsx'
import { useApi } from '../lib/api.js'
import { pickBackground } from '../lib/backgrounds.js'
import { plural } from '../lib/format.js'
import { useSession } from '../lib/session.jsx'
import { viewerTimeZoneName } from '../lib/time.js'

const STATS = [
  { key: 'liveEvents', label: 'Live events', icon: Radio },
  { key: 'openRegistrations', label: 'Open registrations', icon: ClipboardList },
  { key: 'teams', label: 'Teams', icon: Users },
  { key: 'matchesPlayed', label: 'Matches played', icon: Swords },
]

// Quiet lettering in the margins on very wide screens, as on the poster.
function SideNotes() {
  const note = 'text-[9px] leading-[2.1] font-semibold tracking-[0.42em] text-ink-muted/70 uppercase'
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden min-[1640px]:block">
      <div className="absolute bottom-14 left-8 flex gap-4">
        <span className="w-px bg-gradient-to-b from-primary-400 to-transparent" />
        <p className={note}>
          Play
          <br />
          Compete
          <br />
          Belong
        </p>
      </div>
      <div className="absolute top-12 right-8 flex flex-col items-start">
        <p className={note}>
          More than a game
          <br />A higher playground
        </p>
        <span className="mt-5 ml-12 h-24 w-px bg-gradient-to-b from-primary-400/70 to-transparent" />
        <img src="/brand/pred-emblem.png" alt="" className="mt-4 ml-3 size-16 object-contain opacity-60 drop-shadow-[0_0_14px_rgb(249_115_22/0.6)] [filter:sepia(1)_saturate(4)_hue-rotate(-12deg)]" />
      </div>
      <p className={`${note} absolute right-8 bottom-14`}>
        Predecessor
        <br />
        Community driven
        <br />
      </p>
    </div>
  )
}

// The hero's card: the match being played now, then what's next.
function OnNow({ hub, now }) {
  if (!hub) return <Skeleton className="h-96 rounded-2xl" />
  const [live] = hub.liveMatches
  const next = hub.upcoming.slice(0, live ? 2 : 4)
  const moreLive = hub.stats.liveEvents - (live ? 1 : 0)
  return (
    <Card className="space-y-5 p-5">
      {live && (
        <section aria-labelledby="live-title" className="space-y-1.5">
          <h2 id="live-title" className="flex items-center gap-2 text-xs font-bold tracking-[0.16em] text-red-300 uppercase">
            <span aria-hidden="true" className="size-2 rounded-full bg-live animate-live-pulse" /> Live now
          </h2>
          <LiveMatch match={live} />
          {moreLive > 0 && (
            <Link to="/events?status=live" className="inline-block rounded pt-1 text-xs text-ink-muted hover:text-ink">
              {plural(moreLive, 'more event', 'more events')} live →
            </Link>
          )}
        </section>
      )}
      <section aria-labelledby="next-title" className="space-y-3">
        <h2 id="next-title" className="flex items-center gap-2 text-xs font-bold tracking-[0.16em] text-ink-muted uppercase">
          <CalendarClock aria-hidden="true" className="size-3.5" /> Up next
        </h2>
        {next.length ? (
          <UpNextList matches={next} now={now} />
        ) : (
          <EmptyState compact title="Nothing scheduled yet">
            New matches show up here as brackets go live.
          </EmptyState>
        )}
      </section>
      <Button as={Link} to="/calendar?view=week" variant="secondary" className="w-full">
        View full schedule <ArrowRight aria-hidden="true" className="size-4" />
      </Button>
    </Card>
  )
}

function Hero({ hub }) {
  const { user } = useSession()
  const photo = pickBackground('hero', 'home')
  // The server's clock, so relative times agree with what the API filtered on.
  const now = hub ? new Date(hub.generatedAt) : undefined
  return (
    <section aria-labelledby="hero-title" className="relative isolate overflow-hidden border-b border-hairline">
      <div aria-hidden="true" className="bg-landing absolute inset-0 -z-30" />
      {photo && <FadedPhoto src={photo.src} position={photo.position} fade="soft" className="-z-20" />}
      <div
        aria-hidden="true"
        className="dot-grid absolute inset-0 -z-10 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_40%,black,transparent)]"
      />
      <SideNotes />
      <Container className="grid gap-10 pt-12 pb-12 sm:pt-16 lg:grid-cols-[minmax(0,1fr)_24rem] lg:items-center lg:pb-16 xl:grid-cols-[minmax(0,1fr)_27rem]">
        <m.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
          <p className="flex items-center gap-4 text-[11px] font-semibold tracking-[0.4em] text-ink-muted uppercase">
            Compete · Connect · Grow
            <span aria-hidden="true" className="h-px w-14 bg-primary-400" />
          </p>
          <h1 id="hero-title" className="mt-4 font-display text-[2.75rem] leading-[0.92] font-bold tracking-wide uppercase sm:text-6xl lg:text-7xl">
            <span className="block">Predecessor</span>{' '}
            <span className="block bg-gradient-to-r from-primary-300 via-primary-400 to-primary-500 bg-clip-text text-transparent">Tournament Hub</span>
          </h1>
          <p className="mt-5 max-w-xl text-lg text-ink-muted">
            Find tournaments in every region, follow brackets as they happen, and be part of the growing Predecessor competitive community.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button as={Link} to="/events" size="lg">
              Browse events <ArrowRight aria-hidden="true" className="size-4" />
            </Button>
            <Button as={Link} to="/organizers" size="lg" variant="secondary">
              Run your event
            </Button>
            {user ? (
              <Button as={Link} to="/scrims" size="lg" variant="secondary">
                <Swords aria-hidden="true" className="size-4" /> Find a scrim
              </Button>
            ) : (
              <Button as={Link} to="/signin" size="lg" variant="secondary">
                <Compass aria-hidden="true" className="size-4" /> Get started
              </Button>
            )}
          </div>
          <dl className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {STATS.map(({ key, label, icon: Icon }) => (
              <div key={key} className="flex items-center gap-3 rounded-xl bg-surface/70 px-4 py-3.5 ring-1 ring-white/10 ring-inset">
                <Icon aria-hidden="true" className="size-7 shrink-0 text-primary-400" />
                <div className="flex min-w-0 flex-col-reverse">
                  <dt className="text-[10px] leading-snug font-semibold tracking-[0.12em] text-ink-muted uppercase">{label}</dt>
                  <dd className="font-display text-3xl leading-none font-semibold">
                    {hub ? hub.stats[key].toLocaleString() : <Skeleton className="h-7 w-10" />}
                  </dd>
                </div>
              </div>
            ))}
          </dl>
        </m.div>

        <m.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.1 }}>
          <OnNow hub={hub} now={now} />
        </m.div>
      </Container>
    </section>
  )
}

// A section's title, a line about it beside the title, and "View all".
function Heading({ id, icon: Icon, title, description, link }) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-4 gap-y-1">
        <h2 id={id} className="flex items-center gap-2.5 font-display text-2xl font-semibold tracking-wide uppercase">
          <Icon aria-hidden="true" className="size-6 text-primary-400" />
          {title}
        </h2>
        {description && <p className="text-sm text-ink-muted">{description}</p>}
      </div>
      {link && (
        <Link to={link.to} className="group inline-flex shrink-0 items-center gap-1 rounded text-sm font-medium text-primary-300 hover:text-primary-400">
          {link.label}
          <ArrowRight aria-hidden="true" className="size-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
      )}
    </div>
  )
}

// A section with nothing in it yet: one slim row.
function Empty({ icon, title, children }) {
  return (
    <Card>
      <EmptyState compact icon={icon} title={title}>
        {children}
      </EmptyState>
    </Card>
  )
}

function Rows({ rows = 5 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-10" />
      ))}
    </div>
  )
}

function Nothing({ children }) {
  return <p className="px-2 py-6 text-center text-sm text-ink-muted">{children}</p>
}

export default function HomePage() {
  const { data: hub, error, reload } = useApi('/api/hub', { refreshMs: 60_000 })
  const now = hub ? new Date(hub.generatedAt) : undefined

  if (error && !hub) {
    return (
      <>
        <title>Pred Hub · Competitive Predecessor</title>
        <Hero hub={null} />
        <Container className="mt-12">
          <Card>
            <EmptyState
              icon={RefreshCw}
              title="We couldn’t load the hub"
              action={
                <Button variant="secondary" onClick={() => reload()}>
                  Try again
                </Button>
              }
            >
              {error.message}
            </EmptyState>
          </Card>
        </Container>
      </>
    )
  }

  // Wide cards go two to a row, so show an even number.
  const open = hub?.openRegistrations ?? []
  const shownOpen = open.slice(0, open.length >= 4 ? 4 : Math.min(open.length, 2))

  return (
    <>
      <title>Pred Hub · Competitive Predecessor</title>
      <Hero hub={hub} />

      <Container className="mt-8 space-y-10 lg:space-y-12">
        <EventFinder />

        <section aria-labelledby="featured-title">
          <Heading
            id="featured-title"
            icon={Star}
            title="Featured events"
            description="Hand-picked tournaments from across the Predecessor community."
            link={{ to: '/events', label: 'View all events' }}
          />
          {!hub ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-48 rounded-2xl" />
              ))}
            </div>
          ) : hub.featured.length ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {hub.featured.map((event) => (
                <HomeEventCard key={event.slug} event={event} now={now} />
              ))}
            </div>
          ) : (
            <Empty icon={Star} title="No featured events right now" />
          )}
        </section>

        <section aria-labelledby="open-title">
          <Heading
            id="open-title"
            icon={ClipboardList}
            title="Open registrations"
            description="Get your team signed up before these events close."
            link={{ to: '/events?status=open', label: open.length > shownOpen.length ? `View all ${open.length}` : 'View all registrations' }}
          />
          {!hub ? (
            <div className="grid gap-4 lg:grid-cols-2">
              {[0, 1].map((i) => (
                <Skeleton key={i} className="h-36 rounded-2xl" />
              ))}
            </div>
          ) : shownOpen.length ? (
            <div className="grid gap-4 lg:grid-cols-2">
              {shownOpen.map((event) => (
                <HomeRegistrationCard key={event.slug} event={event} now={now} />
              ))}
            </div>
          ) : (
            <Empty icon={ClipboardList} title="No open registrations">
              Check back soon, or follow an organizer to hear when the next one opens.
            </Empty>
          )}
        </section>

        {/* Four compact panels side by side on wide screens. */}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div id="schedule" className="scroll-mt-24">
            <Panel
              id="schedule-title"
              icon={CalendarClock}
              tone="text-secondary-400"
              title="Upcoming schedule"
              description={`Next 7 days · times in ${viewerTimeZoneName()}`}
              link={{ to: '/calendar?view=week', label: 'Calendar' }}
              className="h-full"
            >
              {!hub ? <Rows /> : hub.upcoming.length ? <SchedulePanelList matches={hub.upcoming.slice(0, 5)} /> : <Nothing>No matches scheduled this week.</Nothing>}
            </Panel>
          </div>
          <Panel id="top-teams-title" icon={Trophy} title="Top teams" description="Last 90 days" link={{ to: '/teams', label: 'View all' }}>
            {!hub ? <Rows /> : hub.topTeams.length ? <TopTeamsPanelList teams={hub.topTeams.slice(0, 5)} /> : <Nothing>Teams rank up by placing in events.</Nothing>}
          </Panel>
          <Panel id="results-title" icon={Swords} title="Recent results" description="Last two weeks" link={{ to: '/events?status=completed', label: 'View all' }}>
            {!hub ? <Rows /> : hub.recentResults.length ? <ResultsPanelList matches={hub.recentResults.slice(0, 5)} /> : <Nothing>Scores show up here as matches finish.</Nothing>}
          </Panel>
          <Panel id="news-title" icon={Megaphone} tone="text-secondary-400" title="Announcements" description="News from the Pred Hub team">
            {!hub ? <Rows rows={3} /> : hub.announcements.length ? <AnnouncementsPanelList announcements={hub.announcements} /> : <Nothing>Pred Hub news shows up here.</Nothing>}
          </Panel>
        </div>
      </Container>
    </>
  )
}
